import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import TsGrid from "../../components/tsGrid";
import { CellClickedEvent } from "@ag-grid-community/core";
import { generateRandomNumber } from "../../utils/function";
import useApi from "../../hooks/useApi";
import userConfig from "../../api/userConfigApi";
import { MyContext } from "../../context/myContext";
import { getUserConfigDefs } from "./UserConfigDefs";
import { toast } from "react-toastify";
import { Modal } from "antd";

export const generateRandomId = () => {
    const randomStr = generateRandomNumber().toString(36).substring(2, 15); // Generate a random string
    const timestamp = new Date().getTime(); // Get the current timestamp

    const randomID = `newrec_${randomStr}_${timestamp}`;

    return randomID;
};

const UserConfig: React.FC<any> = () => {
    const gridRef = useRef<any>(null);
    const gridApiRef = useRef<any>(null);
    const containerStyle = useMemo(() => ({ width: '100%', height: '100%', marginBottom: '10px' }), []);
    const gridStyle = useMemo(() => ({ height: '90%', width: '100%', padding: "0px !important" }), []);
    const [rowData, setRowData] = useState<any[]>([])
    const getUsersApi = useApi(userConfig.getUsersList)
    const updateUsersApi = useApi(userConfig.UpdateUser)
    const { user } = useContext<any>(MyContext);
    const [productList, setProductList] = useState<any[]>([]);
    const deleteUserApi = useApi(userConfig.DeleteUser)

    const getUsers = async() => {
        try{
            const users = await getUsersApi.request();

            if(users?.data){
                const list = users?.data?.data?.map((item: any) => {
                    setProductList(user?.productList);
                    return {
                        ...item,
                        username: item?.username?.split("@")[0],
                        domain: `@${getOrganization()}`,
                        allProducts: user?.productList,
                        productIds: item?.productList?.map((pr: any) => pr?.id),
                        productList: item?.productList?.map((pr: any) => pr?.name)?.join(', '),
                        manage_users: item?.permissions?.manage_users ?? false,
                        manage_teams: item?.permissions?.manage_teams ?? false,
                        manage_performance_targets: item?.permissions?.manage_performance_targets ?? false,
                        saveData:saveData
                    }
                })
                
                setRowData([...list, { id: generateRandomId(),  domain: `@${getOrganization()}`}]);
            }
        }
        catch(err){

        }
    }

    useEffect(() => {
        getUsers()
    }, [])

    const defaultColDef = useMemo(() => {
        return {
            resizable: true,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            cellClass: 'border-right',
            filter: true,
            suppressCellSelection: true,
            flex: 1
        };
    }, []);

    const onGridReady = useCallback((params: any) => {
        gridApiRef.current = params.api;
        params.api.sizeColumnsToFit();
    }, []);

    const onCellClicked = (params: CellClickedEvent) => {
        const rowIndex = params.rowIndex || 0;
        const columnKey = params?.column?.getColId();
        const rowNode = gridApiRef.current.getRowNode(rowIndex);
    
        if ((columnKey === "manage_users" || columnKey === "manage_teams" || columnKey === 'manage_performance_targets')) {
            if (rowNode) {
                const data = rowNode.data;
                if(data?.username && data?.productIds?.length){
                data[columnKey] = !data[columnKey];
                gridApiRef.current.applyTransaction({ update: [data] });
                    saveData(params);
                }
            }
        }
    }

    const scrollToBottom = useCallback(() => {
        if (gridApiRef.current) {
            const rowCount = gridApiRef.current.getDisplayedRowCount();
            gridApiRef.current.ensureIndexVisible(rowCount - 1, "bottom");
        }
    }, []);

    const addNewRow = useCallback(() => {

        const newItem = { id: generateRandomId(), domain: `@${getOrganization()}`  };
        if (gridApiRef.current) {
            gridApiRef.current.applyTransaction({ add: [newItem] });
            setTimeout(() => {
                scrollToBottom();
            }, 10);

        }
    }, [rowData, scrollToBottom]);


    const getAllRowsData = (): any[] => {
        const allRows: any[] = [];
        gridApiRef.current.forEachNode((node: any) => {
            if (node.data) {
                allRows.push(node.data);
            }
        });
        return allRows;
    };

    const getOrganization = () => {
        const org = user?.email?.split("@")[1];
        return org;
    }

    const onHandleCellValueChanged = useCallback((params: any) => {
        const rowCount = gridApiRef.current.getDisplayedRowCount();
        const rowNode = gridApiRef.current.getRowNode(params.node.rowIndex);
        const lastRowIndex = rowCount - 1;
        const data = rowNode.data;

        if (params?.colDef?.field === 'username') {
            data['username'] = params?.data?.username;

            const isAlreadyPresent = getAllRowsData()?.find((item: any) => (item?.username?.toLowerCase() === params?.data?.username?.toLowerCase()) && (typeof (item?.id) === 'number'));
            if(isAlreadyPresent){
                data['username'] = '';
                gridApiRef.current.applyTransaction({ update: [data] });
                toast.error('Username already present', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                  });
                return
            }

            if (params.rowIndex === lastRowIndex && data['username']) {
                addNewRow();
                gridApiRef.current.ensureIndexVisible(rowCount - 1, "bottom");
            }
        }
        gridApiRef.current.applyTransaction({ update: [data] });

        if (data?.username) {
            saveData(params);
        }
    }, [])

    const saveData = async (params: any,isInActive?:boolean) => {
        const data = params?.data
        const rowIndex = params?.node?.rowIndex;
        try {
            const payload = {
                id: typeof data?.id === 'number' ? data?.id : 0,
                username: `${data?.username}${data?.domain}`,
                first_name: data?.first_name,
                last_name: data?.last_name,
                permissions: {
                    manage_users: data?.manage_users ?? false,
                    manage_teams: data?.manage_teams ?? false,
                    manage_performance_targets: data?.manage_performance_targets ?? false
                },
                productIds: data?.productIds ?? [],
                productList: data?.productList ?? '',
                is_active: isInActive ?? true
            }
            const response = await updateUsersApi.request(payload);
            if(response?.data?.data){
                const updatedData = { 
                    ...data, 
                    ...response?.data?.data[0],
                    username: data?.username,
                    manage_users: response?.data?.data[0]?.permissions?.manage_users ?? false,
                    manage_teams: response?.data?.data[0]?.permissions?.manage_teams ?? false,
                    manage_performance_targets: response?.data?.data[0]?.permissions?.manage_performance_targets ?? false
                };
                
                setRowData(prev => {
                    const newData = [...prev];
                    newData[rowIndex] = updatedData;
                
                    // Only add a new row if it's the last one
                    if (rowIndex === prev.length - 1) {
                        newData.push({ id: generateRandomId(), domain: `@${getOrganization()}` });
                    }
                
                    return newData;
                });
            }
        }
        catch (err) {

        }
    }

    const afterUpdate = () => {
        getUsers()
    }


    const handleAction = (username: string) => {
        Modal.warning({
            title: `Removing all products will prevent you from logging in.`,
            okText: "Ok",
            onOk:()=>{
                afterUpdate();
            }

        });
    };

    const handleProducts = (params: any) => {
        const rowNode = gridApiRef.current.getRowNode(params.node.rowIndex);
        const data = rowNode.data;
        if (params?.colDef.field === 'products') {
            const productWithNameByUser = productList?.filter((pr: any) => params?.data?.products?.includes(pr.id))?.map((pm: any) => pm.name)?.join(', ');
            if (!params?.data?.products?.length && params?.data?.username + params?.data?.domain === user?.email) {
                handleAction(params?.data?.username);
                return
            }
            data['productIds'] = params?.data?.products;
            data['productList'] = productWithNameByUser;
        }
        gridApiRef.current.applyTransaction({ update: [data] });
    }

    const columnDefs = useMemo(() => getUserConfigDefs({
        afterUpdate,
        handleProducts,
        getOrganization,
        productList,
        user
    }), [addNewRow, rowData, afterUpdate, handleProducts, getOrganization, productList]);


    return (
        <div className="p-10">
            <h1 className="page-heading">User Configuration</h1>
            <div style={containerStyle}>
                <div style={{ height: 'calc(100vh - 100px)' }} className={`ag-theme-alpine grid_style mt-20 user-config-table`}>
                    <TsGrid
                        rowData={rowData}
                        columnDefs={columnDefs}
                        onGridReady={onGridReady}
                        gridRef={gridRef}
                        gridStyle={gridStyle}
                        defaultColDef={defaultColDef}
                        onCellClicked={onCellClicked}
                        onCellValueChanged={onHandleCellValueChanged}
                        editType={"fullRowInline"}
                        singleClickEdit={true}
                        stopEditingWhenCellsLoseFocus={true}
                        virtualization={true}
                    />
                </div>
            </div>
        </div>
    )
}

export default UserConfig;
